import React, { useEffect, useMemo, useState } from "react";
import { Link, useLocation } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import { Hexagon, ChevronRight, X } from "lucide-react";
import { SPONSOR_CTA_ACTIVE, SPONSOR_CTA_VARIANTS } from "@/data/brand";
import { api } from "@/lib/api";
import { getSessionId } from "@/lib/session";

const DISMISS_KEY = "pshl_sponsor_cta_dismissed";
const VARIANT_SESSION_KEY = "pshl_sponsor_cta_variant";
const IMPRESSION_LOGGED_KEY = "pshl_sponsor_cta_impression_logged";
const SHOW_AFTER_PX = 700;

function resolveVariantKey() {
  const keys = Object.keys(SPONSOR_CTA_VARIANTS);
  if (SPONSOR_CTA_ACTIVE === "random") {
    try {
      const stored = sessionStorage.getItem(VARIANT_SESSION_KEY);
      if (stored && SPONSOR_CTA_VARIANTS[stored]) return stored;
      const pick = keys[Math.floor(Math.random() * keys.length)];
      sessionStorage.setItem(VARIANT_SESSION_KEY, pick);
      return pick;
    } catch (err) {
      if (process.env.NODE_ENV !== "production") console.warn("[SponsorStickyCTA] variant pick failed", err);
      return keys[0];
    }
  }
  return SPONSOR_CTA_VARIANTS[SPONSOR_CTA_ACTIVE] ? SPONSOR_CTA_ACTIVE : keys[0];
}

/**
 * Slides up from the bottom of the viewport after the user scrolls past the
 * hero. Desktop-only (hidden below lg). Dismiss is sticky for the session.
 * Auto-hides on /sponsorships and /contact pages to avoid redundancy with the
 * on-page lead form.
 */
export default function SponsorStickyCTA() {
  const [visible, setVisible] = useState(false);
  const [dismissed, setDismissed] = useState(false);
  const { pathname } = useLocation();
  const variantKey = useMemo(() => resolveVariantKey(), []);
  const variant = SPONSOR_CTA_VARIANTS[variantKey];

  // Skip the CTA on pages where the user is already converting.
  const skipped =
    pathname.startsWith("/sponsorships") ||
    pathname.startsWith("/contact") ||
    pathname.startsWith("/admin");

  useEffect(() => {
    if (typeof window === "undefined") return;
    try {
      if (sessionStorage.getItem(DISMISS_KEY) === "1") setDismissed(true);
    } catch (err) { if (process.env.NODE_ENV !== "production") console.warn("[SponsorStickyCTA] dismiss-read failed", err); }
  }, []);

  useEffect(() => {
    if (skipped || dismissed) return;
    const onScroll = () => setVisible(window.scrollY > SHOW_AFTER_PX);
    onScroll();
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => window.removeEventListener("scroll", onScroll);
  }, [skipped, dismissed]);

  // Log impression exactly once per session per variant
  useEffect(() => {
    if (!visible || skipped || dismissed) return;
    try {
      const flag = sessionStorage.getItem(IMPRESSION_LOGGED_KEY);
      if (flag === variantKey) return;
      sessionStorage.setItem(IMPRESSION_LOGGED_KEY, variantKey);
    } catch (err) { if (process.env.NODE_ENV !== "production") console.warn("[SponsorStickyCTA] skip-read failed", err); }
    api.post("/shares", {
      kind: "cta-impression",
      target_id: variantKey,
      page: pathname,
      session_id: getSessionId(),
    }).catch(() => { /* fire-and-forget */ });
  }, [visible, skipped, dismissed, variantKey, pathname]);

  const onCtaClick = () => {
    api.post("/shares", {
      kind: "cta-click",
      target_id: variantKey,
      page: pathname,
      session_id: getSessionId(),
    }).catch(() => { /* fire-and-forget */ });
  };

  // Build a UTM-tagged destination so marketing tools (GA, Plausible, etc.)
  // can attribute traffic by sticky-CTA variant + referring page.
  const ctaHref = useMemo(() => {
    const params = new URLSearchParams({
      utm_source: "sticky-cta",
      utm_medium: "in-app",
      utm_campaign: "sponsor-inventory",
      utm_variant: variantKey,
      utm_page: pathname || "/",
    });
    return `/sponsorships?${params.toString()}#sponsor-lead`;
  }, [variantKey, pathname]);

  const onDismiss = () => {
    setDismissed(true);
    try { sessionStorage.setItem(DISMISS_KEY, "1"); } catch (err) { if (process.env.NODE_ENV !== "production") console.warn("[SponsorStickyCTA] dismiss-write failed", err); }
  };

  if (skipped || dismissed) return null;

  return (
    <AnimatePresence>
      {visible && (
        <motion.div
          initial={{ y: 80, opacity: 0 }}
          animate={{ y: 0, opacity: 1 }}
          exit={{ y: 80, opacity: 0 }}
          transition={{ duration: 0.4, ease: [0.16, 1, 0.3, 1] }}
          data-testid="sponsor-sticky-cta"
          className="hidden lg:block fixed bottom-5 left-1/2 -translate-x-1/2 z-40"
        >
          <div className="relative bg-pshl-surface/95 backdrop-blur-xl border border-white/15 shadow-[0_24px_60px_rgba(0,0,0,0.55)] flex items-center gap-5 pl-5 pr-3 py-3">
            {/* electric-blue glow */}
            <div className="absolute -inset-px pointer-events-none border border-pshl-electric/40" />
            <div className="flex items-center gap-3">
              <div className="h-9 w-9 grid place-items-center bg-pshl-electric/15 border border-pshl-electric/40">
                <Hexagon className="h-4 w-4 text-pshl-electric" />
              </div>
              <div className="leading-tight">
                <div className="text-[10px] uppercase tracking-[0.25em] text-pshl-electric font-display">{variant.eyebrow}</div>
                <div className="font-display text-white text-base tracking-tight">{variant.headline}</div>
              </div>
            </div>
            <div className="hidden xl:block text-xs text-white/65 max-w-[280px] leading-snug">
              {variant.subtext}
            </div>
            <Link
              to={ctaHref}
              onClick={onCtaClick}
              data-testid="sponsor-sticky-cta-button"
              className="inline-flex items-center gap-2 bg-pshl-electric hover:bg-pshl-electric/90 text-white font-display uppercase tracking-[0.18em] text-xs px-5 py-3 transition-all hover:shadow-[0_0_24px_rgba(0,85,255,0.5)] whitespace-nowrap"
            >
              {variant.cta} <ChevronRight className="h-3.5 w-3.5" />
            </Link>
            <button
              type="button"
              onClick={onDismiss}
              aria-label="Dismiss"
              data-testid="sponsor-sticky-cta-dismiss"
              className="h-9 w-9 grid place-items-center text-white/55 hover:text-white hover:bg-white/5 transition-colors"
            >
              <X className="h-4 w-4" />
            </button>
          </div>
        </motion.div>
      )}
    </AnimatePresence>
  );
}
